/**
 * Retention policy and derived expiry (PRD H4 / C8's purge half; increment-9
 * plan §0.1 calls 1–2 and §0.1a).
 *
 * WHY THIS IS PLATFORM LOGIC AND NOT CLINICAL CONTENT. A retention duration is
 * a governance and legal fact — Product, counsel and the IEC decide it, not the
 * clinical team — so it lives here rather than in `@advaitnetra/protocol`,
 * which stays the home of the things OQ6 signs off. The two are marked the same
 * way and for the same reason, but they are different questions with different
 * deciders.
 *
 * THE FIELD NAME SAYS "PROPOSED" FOR THE WHOLE TABLE. `durationDaysProposed`
 * borrows the trick `flags.ts` plays with `paramsProposed`: a consumer cannot
 * reach a duration without typing the word "Proposed" in its own code, and
 * `status` — not the field's spelling — is the authority on whether the purge
 * may execute. Five years (1825 days) was a STARTING FIGURE the owner put up
 * on 2026-08-07 (`AN-RETENTION@0.1`, `proposed`, plan §0.1a). **OQ2 CLOSED on
 * 2026-09-08**: ADR-0029 §4 ratified that term because the patient consent
 * `AN-TKR-consent@1.0` states it to the patient — so `AN-RETENTION@1.0` is
 * `ratified` and is what new study enrolments pin. `@0.1` stays `proposed`
 * forever for the rows that pinned it under the DRAFT consent: the purge
 * executor still refuses those without acknowledgement, exactly as before.
 *
 * EXPIRY IS DERIVED, NEVER STORED. There is no expiry column anywhere and
 * there must never be one: an append-only row cannot honestly carry a date that
 * a later policy edit would change, and a stored expiry is a second source of
 * truth that drifts from the pin it came from (ADR-0010 ext §3 — durations bind
 * to a class, never to a mutable per-row date).
 *
 * AND IT REFUSES RATHER THAN GUESSES. Every path that cannot compute an expiry
 * returns `null` with a named reason: no pin, an unknown policy version, a
 * policy with no duration set, or an episode that never ran a session. **A
 * missing duration must never fall back to a default** — the one place a bug
 * here deletes something. The `durationDaysProposed: null` path stays
 * implemented and tested even though the shipped value is set, because a future
 * policy version may legitimately have none (plan §1, PR2).
 */

import { z } from 'zod';

/**
 * Retention classes, matching `capture_artifacts.retention_class` (migrations
 * 002 and 020). ADR-0004 retains all STUDY material under one umbrella; the
 * class exists so a future per-kind split (video kept shorter than the
 * interpretation stream) is a data change, not a schema change.
 *
 * ⚠ APPEND ONLY — NEVER REORDER. `services/episodes.ts` historically pinned
 * every real enrollment by POSITION (`RETENTION_CLASSES[0]`), so reordering
 * this array would silently re-pin real patients to practice terms and expose
 * their media to a 30-day purge. That call site now names the class through
 * `STUDY_RETENTION_CLASS` below precisely so position stops being load-bearing,
 * but the array is still public API and the ordering rule stands for anyone
 * else indexing it.
 */
export const RETENTION_CLASSES = ['study-full-retention', 'practice-short-retention'] as const;
export type RetentionClass = (typeof RETENTION_CLASSES)[number];
export const retentionClassSchema = z.enum(RETENTION_CLASSES);

/**
 * The class real, consented, enrolled patients are pinned to (`prod`). Named
 * rather than indexed: a positional reference to the wrong element here is the
 * one mistake in this file that shortens a patient's retention without anyone
 * reading a diff line that says so.
 */
export const STUDY_RETENTION_CLASS: RetentionClass = 'study-full-retention';

/**
 * The class practice episodes are pinned to (`train`, ADR-0018). A real
 * volunteer in front of the camera, a synthetic patient record behind it — the
 * media is real personal data and gets a short, ratified, actually-executable
 * expiry rather than the study's parked five-year figure.
 */
export const PRACTICE_RETENTION_CLASS: RetentionClass = 'practice-short-retention';

export type RetentionPolicyStatus = 'proposed' | 'ratified';

export interface RetentionPolicy {
  /** `NAME@major.minor`, pinned per episode at consent and never re-derived. */
  version: string;
  class: RetentionClass;
  /** `proposed` until Product + IEC rule (OQ2). The executor reads this, not a comment. */
  status: RetentionPolicyStatus;
  /**
   * Days from the episode's LAST SESSION until its captured material may be
   * purged. `null` is legal and means **no expiry**, never a default.
   */
  durationDaysProposed: number | null;
  /** What the figure rests on, in one sentence, for anyone who has to defend it. */
  basis: string;
}

/**
 * Every retention policy version the platform knows. Append-only by
 * convention, exactly like the protocol templates: a change in terms is a NEW
 * entry, never an edit of an existing one — episodes pin a version, and a
 * pinned episode must keep resolving to the terms its patient agreed to.
 */
export const RETENTION_POLICIES: readonly RetentionPolicy[] = [
  {
    // SUPERSEDED FOR NEW ENROLMENTS by `AN-RETENTION@1.0` below (2026-09-08),
    // and kept exactly as it was: every consent row captured under the DRAFT
    // consent text pins THIS version, and it must keep resolving to these terms
    // — proposed, never executable without acknowledgement.
    version: 'AN-RETENTION@0.1',
    class: 'study-full-retention',
    status: 'proposed',
    durationDaysProposed: 1825,
    basis:
      'Five years from the episode’s last session — long enough to outlast analysis and ' +
      'publication, short enough to defend under DPDP minimisation. PROPOSED by the owner ' +
      '2026-08-07 as a figure for Product, counsel and the IEC to react to; NOT verified against ' +
      'ICMR guidance, the New Drugs and Clinical Trials Rules or the DPDP Act (OQ2).',
  },
  {
    version: 'AN-RETENTION@1.0',
    class: 'study-full-retention',
    // RATIFIED by the owner's acceptance of ADR-0029 (2026-09-08), decision 4:
    // the patient consent AN-TKR-consent@1.0 states "five years after your
    // last session" as the promise the patient signs, so the number stopped
    // being a proposal the moment that text was accepted. The clinic that
    // signs up is the data fiduciary and consents its own patients on that
    // text; no counsel reviewed it (ADR-0029 §7) — named there, not hidden.
    status: 'ratified',
    durationDaysProposed: 1825,
    basis:
      'Five years from the episode’s last session. RATIFIED by the owner’s acceptance of ' +
      'ADR-0029 (2026-09-08): the consent text AN-TKR-consent@1.0 states this term to the ' +
      'patient, so it is the promise signed, not a figure under review. Same duration as ' +
      'AN-RETENTION@0.1, which stays PROPOSED for the rows captured under the DRAFT consent. ' +
      'No counsel review (ADR-0029 §7).',
  },
  {
    version: 'AN-RETENTION-PRACTICE@1.0',
    class: 'practice-short-retention',
    // RATIFIED, AND BY A DIFFERENT DECIDER THAN OQ2 — read the next paragraph
    // before treating this as a precedent for flipping the study policy.
    //
    // OQ2 asks how long REAL STUDY DATA lives; that belongs to Product, counsel
    // and the IEC, and it stays 'proposed' above until they rule. How long we
    // keep a practice recording of a colleague is an ordinary operational call
    // the owner may make alone, and they made it by ACCEPTING ADR-0018 on
    // 2026-08-13 — the acceptance IS the ratification of these 30 days. The two
    // policies deliberately carry different statuses in the same array to make
    // that distinction unmissable to anyone reading this file.
    status: 'ratified',
    // The field is named `…Proposed` for the whole table (a consumer cannot
    // reach a duration without typing the word), so a ratified policy wears the
    // same name. `status` is the authority on whether it may execute, not this
    // field's spelling — `purgeExecutionGuard` reads `status` and nothing else.
    durationDaysProposed: 30,
    basis:
      'Thirty days from the practice episode’s last session. RATIFIED by the owner’s ' +
      'acceptance of ADR-0018 (2026-08-13), which is the decision of record — this is NOT OQ2 ' +
      'and does NOT bear on how long real study data is kept. Practice media is a real ' +
      'volunteer’s recorded body attached to a synthetic patient record: it has no research ' +
      'purpose, no IEC basis, and therefore no claim to a long term. Short enough that the ' +
      'purge executor runs on it routinely — years before it must work on a patient.',
  },
];

/**
 * The version new STUDY consent rows pin (`prod`) — the rows captured under
 * `AN-TKR-consent@1.0`. Ratified (ADR-0029 §4). Changing terms adds a version;
 * it never edits one.
 */
export const CURRENT_RETENTION_POLICY_VERSION = 'AN-RETENTION@1.0';

/**
 * The version the OQ10 DRAFT consent (`AN-TKR-consent-DRAFT@0.1`) pins, and
 * has always pinned: proposed, so nothing captured under the draft text can be
 * purged without the operator's typed acknowledgement. Named separately from
 * the current constant so `services/episodes.ts` maps each consent text to
 * ITS terms by name — a draft row must never inherit the ratified clock.
 */
export const DRAFT_CONSENT_RETENTION_POLICY_VERSION = 'AN-RETENTION@0.1';

/**
 * The version new PRACTICE consent rows pin (`train`, ADR-0018). Kept separate
 * from the study constant above so no call site can reach the wrong terms by
 * defaulting: an episode pins one or the other deliberately, by name.
 */
export const CURRENT_PRACTICE_RETENTION_POLICY_VERSION = 'AN-RETENTION-PRACTICE@1.0';

export function findRetentionPolicy(version: string): RetentionPolicy | null {
  return RETENTION_POLICIES.find((p) => p.version === version) ?? null;
}

/**
 * The terms an episode is bound to, recorded on its consent at consent time —
 * the same shape `protocol_version` and `consent_text_version` already have.
 * An episode purges on the terms ITS patient agreed to, never on today's
 * constant (plan §0.1 call 2).
 */
export interface RetentionPin {
  class: RetentionClass;
  policyVersion: string;
}

export const retentionPinSchema = z.object({
  class: retentionClassSchema,
  policyVersion: z.string().trim().min(1).max(100),
});

/** Why an expiry could not be derived — named, so a caller never has to guess. */
export type RetentionExpiryReason =
  | 'derived'
  /** The consent row carries no retention terms (pre-017 rows; never backfilled). */
  | 'no-pin'
  /** The pin names a policy version this build does not know. Refuse, do not guess. */
  | 'unknown-policy'
  /** The policy sets no duration. Legal, and it means no expiry — never a default. */
  | 'no-duration'
  /** The episode has never run a session, so the clock has not started. */
  | 'no-anchor';

export interface RetentionExpiry {
  /** ISO date (YYYY-MM-DD), or null when no expiry can be derived. */
  expiresOn: string | null;
  reason: RetentionExpiryReason;
  policy: RetentionPolicy | null;
  /**
   * True when an expiry exists but rests on an unratified duration. The purge
   * executor refuses real data on this; surfaces showing a date must say it.
   */
  proposed: boolean;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Add whole days to an ISO date, in UTC. Dates here are day facts, not instants. */
export function addDaysIso(isoDate: string, days: number): string {
  if (!ISO_DATE.test(isoDate)) throw new Error(`addDaysIso: malformed date ${isoDate}`);
  const start = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(start.getTime())) throw new Error(`addDaysIso: invalid date ${isoDate}`);
  return new Date(start.getTime() + days * DAY_MS).toISOString().slice(0, 10);
}

/**
 * Derive an episode's retention expiry from its pin and the date of its last
 * session. Pure: no clock, no I/O, no defaults — the caller supplies the anchor
 * and gets back either a date or a named refusal.
 */
export function retentionExpiry(input: {
  pin: RetentionPin | null;
  /** ISO date of the episode's last session that actually ran; null if none has. */
  lastSessionDate: string | null;
  /**
   * How a pinned version resolves to terms. Defaults to the table above; it is
   * injectable so the **no-duration** branch can be exercised against a real
   * policy object rather than asserted around. That branch is the one whose
   * wrong answer deletes a patient's video, and a shipped table with a duration
   * set would otherwise leave it permanently untestable — which is how a
   * fallback gets added later by someone who never sees it run.
   */
  resolve?: (version: string) => RetentionPolicy | null;
}): RetentionExpiry {
  if (!input.pin) return { expiresOn: null, reason: 'no-pin', policy: null, proposed: false };

  const policy = (input.resolve ?? findRetentionPolicy)(input.pin.policyVersion);
  if (!policy) {
    return { expiresOn: null, reason: 'unknown-policy', policy: null, proposed: false };
  }

  const proposed = policy.status === 'proposed';
  if (policy.durationDaysProposed === null) {
    return { expiresOn: null, reason: 'no-duration', policy, proposed };
  }
  if (!input.lastSessionDate) {
    return { expiresOn: null, reason: 'no-anchor', policy, proposed };
  }

  return {
    expiresOn: addDaysIso(input.lastSessionDate, policy.durationDaysProposed),
    reason: 'derived',
    policy,
    proposed,
  };
}
